import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import Sidebar from "../../../layout/Sidebar";
import Header from "../../../layout/Header";
import Footer from "../../../layout/Footer";
import { getImageUrl, ageCalculate } from "../../../utils/utils";
import toast from "../../../utils/toast";

const expireCookie = (name) => {
  document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; SameSite=Lax`;
};

const setMemberCookie = (name, value) => {
  const expires = new Date(Date.now() + 3 * 24 * 60 * 60 * 1000).toUTCString();
  document.cookie = `${name}=${encodeURIComponent(JSON.stringify(value))}; expires=${expires}; path=/; SameSite=Lax`;
};

const getMemberPortalBase = () => {
  if (process.env.REACT_APP_MEMBER_URL) return process.env.REACT_APP_MEMBER_URL;

  if (typeof window !== "undefined") {
    const { hostname, protocol } = window.location;
    if (hostname === "localhost" || hostname === "127.0.0.1") {
      return "http://localhost:3001";
    }
    if (hostname.includes("demo2.mightywebber.com") || hostname.includes("demo2.")) {
      return "https://demo3.mightywebber.com";
    }
    return `${protocol}//${hostname}`;
  }

  if (process.env.REACT_APP_HOST_URL) return process.env.REACT_APP_HOST_URL;
  return "http://localhost:3001";
};

export default function ViewMemberDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState("");
  const [activeTab, setActiveTab] = useState("overview");
  const [uploadingPhotoIndex, setUploadingPhotoIndex] = useState(null);

  const API_URL = (process.env.REACT_APP_BASE_URL_API || "http://localhost:8080").replace(/\/+$/, "");

  useEffect(() => {
    fetchUserDetails();
  }, [id]);

  const fetchUserDetails = async () => {
    setLoading(true);
    setError("");
    const token = localStorage.getItem("adminToken");
    try {
      const response = await fetch(`${API_URL}/api/admin/user-list/${id}`, {
        headers: {
          "Authorization": `Bearer ${token}`,
          "Content-Type": "application/json"
        }
      });
      const result = await response.json();
      if (result.status && result.data) {
        setUser(result.data);
      } else {
        setError(result.message || "Failed to load user details");
      }
    } catch (err) {
      console.error("ViewMemberDetails error:", err);
      setError("Error connecting to server. Please ensure backend is running.");
    } finally {
      setLoading(false);
    }
  };

  const handleMemberLogin = async (userData) => {
    if (!userData?._id) {
      toast.showError("Member not found");
      return;
    }

    if (!window.confirm(`Login as member ${userData.profile_id || userData.name || "selected user"}?`)) {
      return;
    }

    // Open tab immediately from user action to reduce popup blocking
    const targetWindow = window.open("", "_blank");
    if (!targetWindow) {
      toast.showError("Popup blocked. Please allow popups and try again.");
      return;
    }

    try {
      targetWindow.opener = null;
    } catch (e) {
      // Ignore if browser blocks opener write
    }

    try {
      const token = localStorage.getItem("adminToken");
      const url = `${API_URL.replace(/\/$/, "")}/api/admin/member-login-as-user/${userData._id}`;
      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      const result = await response.json();

      if (!response.ok || !result.status) {
        targetWindow.close();
        toast.showError(result.message || "Unable to login as member");
        return;
      }

      expireCookie("authTokenUser");
      expireCookie("authTokenUserLogin");
      expireCookie("otp-data");
      setMemberCookie("authTokenUserLogin", JSON.stringify(result.token));

      const redirectPath = result?.data?.redirect_url || "/dashboard";
      const basePortalUrl = getMemberPortalBase()
        .replace(/\/$/, "")
        .replace(/\/admin$/, "");
      const targetUrl = new URL(redirectPath, `${basePortalUrl}/`);
      targetUrl.searchParams.set("adminToken", result.token);

      targetWindow.location.replace(targetUrl.toString());
      targetWindow.focus();

      toast.showSuccess(result.message || "Member logged in successfully");
    } catch (error) {
      targetWindow.close();
      console.error("Member login error:", error);
      toast.showError("Unable to login as member");
    }
  };

  const handleSoftDeleteMember = async () => {
    if (!window.confirm(`Are you sure you want to soft delete member ${user?.profile_id || user?.name || "profile"}? Profile will be hidden from searches and matches, and can be restored.`)) {
      return;
    }

    setDeleting(true);
    try {
      const token = localStorage.getItem("adminToken");
      const res = await fetch(`${API_URL}/api/admin/user-list/soft-delete/${id}`, {
        method: "POST",
        headers: {
          "Authorization": `Bearer ${token}`,
          "Content-Type": "application/json"
        }
      });
      const result = await res.json();
      if (result.status || result.success) {
        toast.showSuccess("Member soft deleted successfully");
        fetchMemberDetails();
      } else {
        toast.showError(result.message || "Failed to soft delete member");
      }
    } catch (err) {
      console.error("Soft delete error:", err);
      toast.showError("Server error soft deleting member");
    } finally {
      setDeleting(false);
    }
  };

  const handleRestoreMember = async () => {
    if (!window.confirm(`Are you sure you want to restore member ${user?.profile_id || user?.name || "profile"}?`)) {
      return;
    }

    setDeleting(true);
    try {
      const token = localStorage.getItem("adminToken");
      const res = await fetch(`${API_URL}/api/admin/user-list/restore/${id}`, {
        method: "POST",
        headers: {
          "Authorization": `Bearer ${token}`,
          "Content-Type": "application/json"
        }
      });
      const result = await res.json();
      if (result.status || result.success) {
        toast.showSuccess("Member restored successfully");
        fetchMemberDetails();
      } else {
        toast.showError(result.message || "Failed to restore member");
      }
    } catch (err) {
      console.error("Restore error:", err);
      toast.showError("Server error restoring member");
    } finally {
      setDeleting(false);
    }
  };

  const handleDeleteMember = async () => {
    if (!window.confirm(`WARNING: Are you sure you want to PERMANENTLY delete member ${user?.profile_id || user?.name || "profile"}? All profile data and photos will be permanently deleted from the database. This cannot be undone.`)) {
      return;
    }

    setDeleting(true);
    try {
      const token = localStorage.getItem("adminToken");
      const res = await fetch(`${API_URL}/api/admin/user-list/permanent-delete/${id}`, {
        method: "DELETE",
        headers: {
          "Authorization": `Bearer ${token}`,
          "Content-Type": "application/json"
        }
      });
      const result = await res.json();
      if (result.status || result.success) {
        toast.showSuccess("Member permanently deleted successfully");
        navigate("/user/all-member");
      } else {
        toast.showError(result.message || "Failed to delete member");
      }
    } catch (err) {
      console.error("Delete error:", err);
      toast.showError("Server error deleting member");
    } finally {
      setDeleting(false);
    }
  };

  const handleDeletePhoto = async (photoIndex) => {
    if (!window.confirm(`Are you sure you want to delete Photo ${photoIndex === 0 ? "Main" : photoIndex}?`)) {
      return;
    }

    try {
      const token = localStorage.getItem("adminToken");
      const res = await fetch(`${API_URL}/api/admin/user-list/${id}/photo/${photoIndex}`, {
        method: "DELETE",
        headers: {
          "Authorization": `Bearer ${token}`
        }
      });
      const data = await res.json();
      if (data.status) {
        toast.showSuccess(`Photo ${photoIndex === 0 ? "Main" : photoIndex} deleted successfully`);
        fetchUserDetails();
      } else {
        toast.showError(data.message || "Failed to delete photo");
      }
    } catch (err) {
      console.error("Delete photo error:", err);
      toast.showError("Error deleting photo");
    }
  };

  const handleUploadPhoto = async (e, photoIndex) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploadingPhotoIndex(photoIndex);
    try {
      const uploadData = new FormData();
      uploadData.append("file", file);

      const uploadRes = await fetch(`${API_URL}/api/upload`, {
        method: "POST",
        body: uploadData
      });
      const uploadResult = await uploadRes.json();

      if (uploadResult.status && (uploadResult.data?.key || uploadResult.data?.url)) {
        const photoKey = uploadResult.data.key || uploadResult.data.url;
        const fieldName = photoIndex === 0 ? "photo" : `photo${photoIndex}`;

        const token = localStorage.getItem("adminToken");
        const updateRes = await fetch(`${API_URL}/api/admin/user-list/update/${id}`, {
          method: "POST",
          headers: {
            "Authorization": `Bearer ${token}`,
            "Content-Type": "application/json"
          },
          body: JSON.stringify({ [fieldName]: photoKey })
        });
        const updateData = await updateRes.json();
        if (updateData.status) {
          toast.showSuccess(`Photo ${photoIndex === 0 ? "Main" : photoIndex} updated successfully`);
          fetchUserDetails();
        } else {
          toast.showError(updateData.message || "Failed to link photo to user");
        }
      } else {
        toast.showError(uploadResult.message || "Photo upload failed");
      }
    } catch (err) {
      console.error("Photo upload error:", err);
      toast.showError("Error uploading photo");
    } finally {
      setUploadingPhotoIndex(null);
      e.target.value = "";
    }
  };

  const getDefaultAvatar = (gender) => {
    const pubUrl = process.env.PUBLIC_URL || "";
    if (gender === "Female") return `${pubUrl}/assets/images/female-icon.png`;
    if (gender === "Male") return `${pubUrl}/assets/images/male-icon.png`;
    return `${pubUrl}/assets/images/user.png`;
  };

  const tabBtn = (tabKey, icon, label) => {
    const isActive = activeTab === tabKey;
    return (
      <button
        type="button"
        onClick={() => setActiveTab(tabKey)}
        style={{
          padding: "10px 20px",
          fontSize: "13px",
          fontWeight: "600",
          borderRadius: "30px",
          border: isActive ? "1px solid #b82e66" : "1px solid #e2e8f0",
          background: isActive
            ? "linear-gradient(135deg, #b82e66 0%, #8e1948 100%)"
            : "#ffffff",
          color: isActive ? "#ffffff" : "#4a5568",
          cursor: "pointer",
          transition: "all 0.25s ease",
          display: "inline-flex",
          alignItems: "center",
          gap: "8px",
          boxShadow: isActive ? "0 4px 12px rgba(184, 46, 102, 0.25)" : "0 1px 3px rgba(0,0,0,0.04)"
        }}
      >
        <i className={icon} style={{ fontSize: "14px", color: isActive ? "#ffffff" : "#b82e66" }}></i>
        <span>{label}</span>
      </button>
    );
  };

  const valOrMuted = (val) => val && String(val).trim() !== "" ? val : <span className="text-muted fst-italic" style={{ color: "#9ca3af" }}>Not provided</span>;

  const formatHeight = (val) => {
    if (!val) return "";
    const s = String(val).trim();
    if (s.includes(".")) {
      const parts = s.split(".");
      const ft = parts[0];
      const inch = parts[1];
      if (inch === "0" || !inch) return `${ft} ft`;
      return `${ft} ft ${inch} in`;
    }
    const n = parseFloat(s);
    if (!isNaN(n)) {
      if (n >= 3 && n <= 8) return `${n} ft`;
      if (n > 30) return `${n} cm`;
    }
    return s;
  };

  const formatIncome = (val) => {
    if (!val) return "";
    const s = String(val).trim();
    const map = {
      "0-100000": "Upto 1 Lakh",
      "100000": "1 Lakh",
      "100000-200000": "1 Lakh - 2 Lakh",
      "200000": "2 Lakh",
      "200000-400000": "2 Lakh - 4 Lakh",
      "300000": "3 Lakh",
      "400000": "4 Lakh",
      "400000-700000": "4 Lakh - 7 Lakh",
      "500000": "5 Lakh",
      "600000": "6 Lakh",
      "700000": "7 Lakh",
      "800000": "8 Lakh",
      "900000": "9 Lakh",
      "1000000": "10 Lakh",
      "1200000": "12 Lakh",
      "1500000": "15 Lakh",
      "1500000-2000000": "15 Lakh - 20 Lakh",
      "2000000": "20 Lakh",
      "2000000-3000000": "20 Lakh - 30 Lakh",
      "2500000": "25 Lakh",
      "3000000-5000000": "30 Lakh - 50 Lakh",
      "3500000": "35 Lakh",
      "5000000": "50 Lakh",
      "5000000-7500000": "50 Lakh - 75 Lakh",
      "7000000": "70 Lakh",
      "7500000-10000000": "75 Lakh - 1 Crore",
      "10000000": "1 Crore",
      "10000000-30000000": "1 Crore - 3 Crore",
      "30000000-50000000": "3 Crore - 5 Crore",
      "Above 10000000": "Above 1 Crore",
      "Above 50000000": "Above 5 Crore",
      "Not Applicable": "Not Applicable"
    };
    if (map[s]) return map[s];
    if (s.includes("-")) {
      const parts = s.split("-");
      const minStr = map[parts[0]] || parts[0];
      const maxStr = map[parts[1]] || parts[1];
      return `${minStr} - ${maxStr}`;
    }
    return s;
  };

  const photoList = [
    { key: "photo", label: "Main Profile Photo", file: user?.photo, index: 0 },
    { key: "photo1", label: "Photo 1", file: user?.photo1, index: 1 },
    { key: "photo2", label: "Photo 2", file: user?.photo2, index: 2 },
    { key: "photo3", label: "Photo 3", file: user?.photo3, index: 3 },
    { key: "photo4", label: "Photo 4", file: user?.photo4, index: 4 },
  ];

  return (
    <div className="container body">
      <div className="main_container">
        <Sidebar />
        <Header pageTitle="View Member Details" />

        <div className="right_col" role="main" style={{ minHeight: "100vh", padding: "20px 25px", background: "#f4f6f9" }}>
          <div className="clearfix" />

          {/* Top Bar Header */}
          <div className="row mb-3 align-items-center">
            <div className="col-md-5 col-sm-6">
              <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                <div style={{ width: "36px", height: "36px", borderRadius: "8px", background: "linear-gradient(135deg, #b82e66 0%, #8e1948 100%)", display: "flex", alignItems: "center", justifyContent: "center", color: "#fff" }}>
                  <i className="fa fa-user" style={{ fontSize: "18px" }}></i>
                </div>
                <div>
                  <h3 style={{ margin: 0, fontWeight: "700", color: "#2A3F54", fontSize: "20px", display: "flex", alignItems: "center", gap: "8px" }}>
                    Member Profile View
                    {user?.isDeleted && (
                      <span style={{ backgroundColor: "#d9534f", color: "#fff", fontSize: "11px", padding: "2px 8px", borderRadius: "12px", fontWeight: "600" }}>
                        Soft Deleted
                      </span>
                    )}
                  </h3>
                  <span style={{ fontSize: "12px", color: "#6c757d" }}>
                    Detailed profile overview & admin actions
                    {user?.isDeleted && user?.deletedAt && ` (Deleted on ${new Date(user.deletedAt).toLocaleDateString()})`}
                  </span>
                </div>
              </div>
            </div>
            <div className="col-md-7 col-sm-6 d-flex justify-content-end align-items-center flex-wrap gap-2">
              <Link to="/user/all-member" className="btn btn-sm" style={{ borderRadius: "20px", background: "#ffffff", border: "1px solid #cbd5e1", color: "#475569", fontWeight: "600", padding: "6px 16px" }}>
                <i className="fa fa-arrow-left me-1"></i> Back to Members
              </Link>
              {user && (
                <>
                  <button
                    type="button"
                    onClick={() => handleMemberLogin(user)}
                    className="btn btn-sm btn-info text-white"
                    style={{ borderRadius: "20px", fontWeight: "600", padding: "6px 14px" }}
                  >
                    <i className="fa fa-sign-in me-1"></i> Login Member
                  </button>
                  <Link
                    to={`/user/edit-member/${user._id}`}
                    className="btn btn-sm text-white"
                    style={{ borderRadius: "20px", background: "linear-gradient(135deg, #b82e66 0%, #8e1948 100%)", border: "none", fontWeight: "600", padding: "6px 18px", boxShadow: "0 3px 8px rgba(184,46,102,0.3)" }}
                  >
                    <i className="fa fa-pencil me-1"></i> Edit Profile
                  </Link>
                  {user.isDeleted ? (
                    <button
                      type="button"
                      onClick={handleRestoreMember}
                      disabled={deleting}
                      className="btn btn-sm btn-success"
                      style={{ borderRadius: "20px", fontWeight: "600", padding: "6px 16px" }}
                    >
                      <i className="fa fa-undo me-1"></i> Restore Member
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={handleSoftDeleteMember}
                      disabled={deleting}
                      className="btn btn-sm btn-warning text-dark"
                      style={{ borderRadius: "20px", fontWeight: "600", padding: "6px 16px" }}
                    >
                      <i className="fa fa-ban me-1"></i> Soft Delete
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={handleDeleteMember}
                    disabled={deleting}
                    className="btn btn-sm btn-danger"
                    style={{ borderRadius: "20px", fontWeight: "600", padding: "6px 16px" }}
                    title="Permanent hard deletion (Admin / Agent only)"
                  >
                    {deleting ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-1" role="status"></span>
                        Deleting...
                      </>
                    ) : (
                      <>
                        <i className="fa fa-trash me-1"></i> Permanent Delete
                      </>
                    )}
                  </button>
                </>
              )}
            </div>
          </div>

          {loading ? (
            <div className="x_panel text-center p-5" style={{ borderRadius: "12px", border: "none", boxShadow: "0 4px 15px rgba(0,0,0,0.05)" }}>
              <div className="spinner-border mb-3" style={{ color: "#b82e66", width: "3rem", height: "3rem" }} role="status"></div>
              <h4 style={{ color: "#2A3F54", fontWeight: "600" }}>Loading member profile...</h4>
            </div>
          ) : error ? (
            <div className="x_panel text-center p-5" style={{ borderRadius: "12px", border: "none", boxShadow: "0 4px 15px rgba(0,0,0,0.05)" }}>
              <div className="text-danger mb-3">
                <i className="fa fa-exclamation-triangle fa-3x" style={{ color: "#e11d48" }}></i>
              </div>
              <h4 className="text-danger">{error}</h4>
              <Link to="/user/all-member" className="btn btn-secondary btn-sm mt-3" style={{ borderRadius: "20px" }}>Back to Members List</Link>
            </div>
          ) : user ? (
            <>
              {/* Member Hero Summary Card */}
              <div className="x_panel mb-4 shadow-sm" style={{ borderRadius: "14px", border: "none", background: "#ffffff", padding: "20px 25px", borderLeft: "5px solid #b82e66" }}>
                <div className="x_content p-0">
                  <div className="row align-items-center">
                    <div className="col-md-2 col-sm-3 text-center mb-3 mb-md-0">
                      <div style={{ position: "relative", display: "inline-block", width: "130px", height: "130px" }}>
                        {/* Circular Progress Ring SVG */}
                        <svg
                          style={{
                            position: "absolute",
                            top: 0,
                            left: 0,
                            width: "130px",
                            height: "130px",
                            transform: "rotate(-90deg)",
                            zIndex: 1
                          }}
                        >
                          <circle
                            cx="65"
                            cy="65"
                            r="58"
                            fill="none"
                            stroke="#e2e8f0"
                            strokeWidth="5"
                          />
                          <circle
                            cx="65"
                            cy="65"
                            r="58"
                            fill="none"
                            stroke="#b82e66"
                            strokeWidth="5"
                            strokeDasharray={2 * Math.PI * 58}
                            strokeDashoffset={
                              (2 * Math.PI * 58) -
                              ((2 * Math.PI * 58) * Math.min(100, Math.max(0, user.profile_completion_pct || 0))) / 100
                            }
                            strokeLinecap="round"
                            style={{ transition: "stroke-dashoffset 0.5s ease" }}
                          />
                        </svg>

                        {/* Avatar Image */}
                        <img
                          src={user.photo ? getImageUrl(user.photo) : getDefaultAvatar(user.gender)}
                          alt={user.name || "Member Avatar"}
                          style={{
                            position: "absolute",
                            top: "7px",
                            left: "7px",
                            width: "116px",
                            height: "116px",
                            borderRadius: "50%",
                            objectFit: "cover",
                            background: "#ffffff",
                            zIndex: 2
                          }}
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = getDefaultAvatar(user.gender);
                          }}
                        />

                        {/* Percentage Pill Badge on Avatar */}
                        <span
                          style={{
                            position: "absolute",
                            bottom: "-6px",
                            left: "50%",
                            transform: "translateX(-50%)",
                            background: "linear-gradient(135deg, #b82e66 0%, #8e1948 100%)",
                            color: "#ffffff",
                            fontSize: "11px",
                            fontWeight: "800",
                            padding: "2px 10px",
                            borderRadius: "12px",
                            border: "2px solid #ffffff",
                            boxShadow: "0 2px 6px rgba(0,0,0,0.2)",
                            zIndex: 3,
                            whiteSpace: "nowrap"
                          }}
                        >
                          {user.profile_completion_pct ? `${user.profile_completion_pct}%` : "0%"}
                        </span>
                      </div>
                    </div>

                    <div className="col-md-7 col-sm-6">
                      <div className="d-flex align-items-center gap-2 mb-1 flex-wrap">
                        <h2 style={{ margin: 0, fontWeight: "800", color: "#1e293b", fontSize: "24px" }}>
                          {user.name || "Unnamed Profile"}
                        </h2>
                        <span style={{ fontSize: "11px", fontWeight: "700", padding: "4px 10px", borderRadius: "20px", background: user.status === 'Active' ? '#dcfce7' : '#fee2e2', color: user.status === 'Active' ? '#15803d' : '#b91c1c' }}>
                          {user.status || 'Active'}
                        </span>
                        <span style={{ fontSize: "11px", fontWeight: "700", padding: "4px 10px", borderRadius: "20px", background: user.profileApproval === 'Approved' ? '#fce7f3' : '#fef3c7', color: user.profileApproval === 'Approved' ? '#be185d' : '#b45309' }}>
                          {user.profileApproval || 'Pending Approval'}
                        </span>
                      </div>

                      <div style={{ margin: "6px 0", fontSize: "14px", color: "#475569" }}>
                        <span style={{ fontWeight: "700", color: "#b82e66", background: "#fdf2f8", padding: "2px 8px", borderRadius: "6px", marginRight: "8px" }}>
                          <i className="fa fa-id-badge me-1 mr-1"></i> {user.profile_id || "VS-N/A"}
                        </span>
                        <span className="me-2"><strong>Gender:</strong> {user.gender || "-"}</span> |
                        <span className="mx-2"><strong>Age:</strong> {user.dob ? `${ageCalculate(user.dob)} Yrs` : "-"}</span> |
                        <span className="ms-2"><strong>Registration Step:</strong> <span style={{ color: (user.step || 0) >= 11 ? "#16a34a" : "#b82e66", fontWeight: "700" }}>{Math.min(user.step || 0, 11)}</span> / 11</span>
                      </div>

                      <div className="mt-3" style={{ display: "flex", flexWrap: "wrap", alignItems: "center" }}>
                        <span style={{ display: "inline-flex", alignItems: "center", gap: "6px", background: "#f0fdf4", padding: "4px 12px", borderRadius: "20px", border: "1px solid #bbf7d0", color: "#166534", fontSize: "13px", marginRight: "12px", marginBottom: "6px" }}>
                          <i className="fa fa-phone" style={{ color: "#16a34a", marginRight: "4px" }}></i>
                          <strong>{user.mobile || user.contact_no || "N/A"}</strong>
                        </span>
                        <span style={{ display: "inline-flex", alignItems: "center", gap: "6px", background: "#eff6ff", padding: "4px 12px", borderRadius: "20px", border: "1px solid #bfdbfe", color: "#1e40af", fontSize: "13px", marginRight: "12px", marginBottom: "6px" }}>
                          <i className="fa fa-envelope" style={{ color: "#2563eb", marginRight: "4px" }}></i>
                          <strong>{user.email || user.contact_email || "N/A"}</strong>
                        </span>
                        <span style={{ display: "inline-flex", alignItems: "center", gap: "6px", background: "#fef2f2", padding: "4px 12px", borderRadius: "20px", border: "1px solid #fecaca", color: "#991b1b", fontSize: "13px", marginBottom: "6px" }}>
                          <i className="fa fa-map-marker" style={{ color: "#dc2626", marginRight: "4px" }}></i>
                          <span>{user.loc_city?.name || "City"}, {user.loc_state?.name || "State"}</span>
                        </span>
                      </div>
                    </div>

                    <div className="col-md-3 col-sm-3 text-end">
                      <div className="p-3 mb-2" style={{ background: "#fdf2f8", borderRadius: "10px", border: "1px dashed #f472b6", textAlign: "center" }}>
                        <span style={{ fontSize: "12px", color: "#831843", fontWeight: "600", display: "block" }}>Profile Completion</span>
                        <span style={{ fontSize: "20px", fontWeight: "800", color: "#b82e66" }}>
                          {user.profile_completion_pct ? `${user.profile_completion_pct}%` : "0%"}
                        </span>
                      </div>
                      <div style={{ textAlign: "center" }}>
                        <span style={{ fontSize: "11px", color: "#64748b", display: "block" }}>Membership Status</span>
                        {user.userPlan?.plan_id?.name ? (
                          <span className="badge" style={{ background: "linear-gradient(135deg, #d97706 0%, #b45309 100%)", color: "#ffffff", fontSize: "12px", padding: "6px 14px", borderRadius: "20px", boxShadow: "0 2px 6px rgba(217,119,6,0.3)" }}>
                            <i className="fa fa-crown me-1" style={{ color: "#fde68a" }}></i> {user.userPlan.plan_id.name} Plan ({user.userPlan.status || "Paid"})
                          </span>
                        ) : (
                          <span className="badge" style={{ background: "#475569", color: "#ffffff", fontSize: "12px", padding: "5px 12px", borderRadius: "20px" }}>
                            Free Member
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Modern Brand Navigation Tabs */}
              <div className="mb-4 d-flex flex-wrap gap-2">
                {tabBtn("overview", "fa fa-id-card", "Personal & Basic Details")}
                {tabBtn("contact", "fa fa-phone", "Contact & Location")}
                {tabBtn("religion", "fa fa-moon-o", "Religion & Horoscope")}
                {tabBtn("education", "fa fa-graduation-cap", "Education & Career")}
                {tabBtn("family", "fa fa-users", "Family & Partner Preferences")}
                {tabBtn("photos", "fa fa-picture-o", "Photos Gallery")}
              </div>

              {/* TAB CONTENT PANELS */}

              {/* 1. PERSONAL & BASIC DETAILS */}
              {activeTab === "overview" && (
                <div className="x_panel shadow-sm" style={{ borderRadius: "12px", border: "none", background: "#ffffff" }}>
                  <div className="x_title d-flex justify-content-between align-items-center" style={{ borderBottom: "2px solid #fce7f3", paddingBottom: "12px" }}>
                    <h4 style={{ margin: 0, fontWeight: "700", color: "#b82e66", fontSize: "16px" }}>
                      <i className="fa fa-user me-2"></i> Personal & Basic Information
                    </h4>
                    <Link to={`/user/edit-member/${user._id}`} className="btn btn-sm text-white" style={{ background: "linear-gradient(135deg, #b82e66 0%, #8e1948 100%)", borderRadius: "20px", fontWeight: "600", fontSize: "12px", padding: "4px 14px" }}>
                      <i className="fa fa-pencil me-1"></i> Edit Profile
                    </Link>
                  </div>
                  <div className="x_content p-2">
                    <table className="table table-bordered mb-0" style={{ borderColor: "#f1f5f9" }}>
                      <tbody>
                        <tr><th style={{ width: "32%", background: "#fdf2f8", color: "#831843", fontWeight: "600" }}>Profile Created For</th><td>{valOrMuted(user.profile_for?.name)}</td></tr>
                        <tr><th style={{ background: "#fdf2f8", color: "#831843", fontWeight: "600" }}>Full Name</th><td><strong>{valOrMuted(user.name)}</strong></td></tr>
                        <tr><th style={{ background: "#fdf2f8", color: "#831843", fontWeight: "600" }}>Gender</th><td>{valOrMuted(user.gender)}</td></tr>
                        <tr><th style={{ background: "#fdf2f8", color: "#831843", fontWeight: "600" }}>Date of Birth</th><td>{user.dob ? `${new Date(user.dob).toLocaleDateString()} (${ageCalculate(user.dob)} Years)` : <span className="text-muted fst-italic">Not provided</span>}</td></tr>
                        <tr><th style={{ background: "#fdf2f8", color: "#831843", fontWeight: "600" }}>Marital Status</th><td>{valOrMuted(user.marital_status?.name)}</td></tr>
                        <tr><th style={{ background: "#fdf2f8", color: "#831843", fontWeight: "600" }}>Have Children</th><td>{valOrMuted(user.have_children)}</td></tr>
                        <tr><th style={{ background: "#fdf2f8", color: "#831843", fontWeight: "600" }}>Height</th><td>{user.height ? formatHeight(user.height) : <span className="text-muted fst-italic">Not provided</span>}</td></tr>
                        <tr><th style={{ background: "#fdf2f8", color: "#831843", fontWeight: "600" }}>Weight</th><td>{user.weight ? `${user.weight} kg` : <span className="text-muted fst-italic">Not provided</span>}</td></tr>
                        <tr><th style={{ background: "#fdf2f8", color: "#831843", fontWeight: "600" }}>Complexion</th><td>{valOrMuted(user.complexion?.name)}</td></tr>
                        <tr><th style={{ background: "#fdf2f8", color: "#831843", fontWeight: "600" }}>Blood Group</th><td>{valOrMuted(user.blood_group)}</td></tr>
                        <tr><th style={{ background: "#fdf2f8", color: "#831843", fontWeight: "600" }}>Manglik</th><td>{valOrMuted(user.manglik)}</td></tr>
                        <tr><th style={{ background: "#fdf2f8", color: "#831843", fontWeight: "600" }}>Disability</th><td>{valOrMuted(user.disability)}</td></tr>
                        <tr><th style={{ background: "#fdf2f8", color: "#831843", fontWeight: "600" }}>Health Information</th><td>{valOrMuted(user.health_information)}</td></tr>
                        <tr><th style={{ background: "#fdf2f8", color: "#831843", fontWeight: "600" }}>Habits (Drinking / Smoking)</th><td>{valOrMuted(user.drinking)} (Drinking) / {valOrMuted(user.smoking)} (Smoking)</td></tr>
                        <tr><th style={{ background: "#fdf2f8", color: "#831843", fontWeight: "600" }}>About Me</th><td>{valOrMuted(user.about)}</td></tr>
                        <tr>
                          <th style={{ background: "#fdf2f8", color: "#831843", fontWeight: "600" }}>Membership Plan</th>
                          <td>
                            {user.userPlan?.plan_id?.name ? (
                              <div style={{ display: "flex", alignItems: "center", gap: "10px", flexWrap: "wrap" }}>
                                <span className="badge" style={{ background: "linear-gradient(135deg, #d97706 0%, #b45309 100%)", color: "#fff", fontSize: "12px", padding: "5px 12px", borderRadius: "14px" }}>
                                  👑 {user.userPlan.plan_id.name} Plan (₹{user.userPlan.price || user.userPlan.plan_id.price || 0})
                                </span>
                                <span style={{ fontSize: "13px", color: "#475569" }}>
                                  Status: <strong style={{ color: "#16a34a" }}>{user.userPlan.status || "Paid"}</strong> |
                                  Contact Views: <strong>{user.userPlan.view_contact || "Unlimited"}</strong> |
                                  Chat: <strong>{user.userPlan.chat || "Unlimited"}</strong>
                                </span>
                              </div>
                            ) : (
                              <span className="text-muted fst-italic">Free Member (No active paid package)</span>
                            )}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* 2. CONTACT & LOCATION DETAILS */}
              {activeTab === "contact" && (
                <div className="x_panel shadow-sm" style={{ borderRadius: "12px", border: "none", background: "#ffffff" }}>
                  <div className="x_title d-flex justify-content-between align-items-center" style={{ borderBottom: "2px solid #fce7f3", paddingBottom: "12px" }}>
                    <h4 style={{ margin: 0, fontWeight: "700", color: "#b82e66", fontSize: "16px" }}>
                      <i className="fa fa-phone me-2"></i> Contact & Location Details
                    </h4>
                    <Link to={`/user/edit-member/${user._id}`} className="btn btn-sm text-white" style={{ background: "linear-gradient(135deg, #b82e66 0%, #8e1948 100%)", borderRadius: "20px", fontWeight: "600", fontSize: "12px", padding: "4px 14px" }}>
                      <i className="fa fa-pencil me-1"></i> Edit Profile
                    </Link>
                  </div>
                  <div className="x_content p-2">
                    <table className="table table-bordered mb-0" style={{ borderColor: "#f1f5f9" }}>
                      <tbody>
                        <tr><th style={{ width: "32%", background: "#fdf2f8", color: "#831843", fontWeight: "600" }}>Login Mobile Number</th><td><strong>{valOrMuted(user.mobile)}</strong></td></tr>
                        <tr><th style={{ background: "#fdf2f8", color: "#831843", fontWeight: "600" }}>Contact Phone (Alternate)</th><td>{valOrMuted(user.contact_no)}</td></tr>
                        <tr><th style={{ background: "#fdf2f8", color: "#831843", fontWeight: "600" }}>Login Email</th><td><strong>{valOrMuted(user.email)}</strong></td></tr>
                        <tr><th style={{ background: "#fdf2f8", color: "#831843", fontWeight: "600" }}>Contact Email (Alternate)</th><td>{valOrMuted(user.contact_email)}</td></tr>
                        <tr><th style={{ background: "#fdf2f8", color: "#831843", fontWeight: "600" }}>Nationality</th><td>{valOrMuted(user.loc_nationality?.name || "Indian")}</td></tr>
                        <tr><th style={{ background: "#fdf2f8", color: "#831843", fontWeight: "600" }}>Current State</th><td>{valOrMuted(user.loc_state?.name)}</td></tr>
                        <tr><th style={{ background: "#fdf2f8", color: "#831843", fontWeight: "600" }}>Current City</th><td>{valOrMuted(user.loc_city?.name)}</td></tr>
                        <tr><th style={{ background: "#fdf2f8", color: "#831843", fontWeight: "600" }}>Pincode</th><td>{valOrMuted(user.loc_pincode)}</td></tr>
                        <tr><th style={{ background: "#fdf2f8", color: "#831843", fontWeight: "600" }}>Residence Type</th><td>{valOrMuted(user.loc_residence_type)}</td></tr>
                        <tr><th style={{ background: "#fdf2f8", color: "#831843", fontWeight: "600" }}>Permanent House Type</th><td>{valOrMuted(user.loc_house_type)}</td></tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* 3. RELIGION & HOROSCOPE */}
              {activeTab === "religion" && (
                <div className="x_panel shadow-sm" style={{ borderRadius: "12px", border: "none", background: "#ffffff" }}>
                  <div className="x_title d-flex justify-content-between align-items-center" style={{ borderBottom: "2px solid #fce7f3", paddingBottom: "12px" }}>
                    <h4 style={{ margin: 0, fontWeight: "700", color: "#b82e66", fontSize: "16px" }}>
                      <i className="fa fa-moon-o me-2"></i> Religion & Astrology Details
                    </h4>
                    <Link to={`/user/edit-member/${user._id}`} className="btn btn-sm text-white" style={{ background: "linear-gradient(135deg, #b82e66 0%, #8e1948 100%)", borderRadius: "20px", fontWeight: "600", fontSize: "12px", padding: "4px 14px" }}>
                      <i className="fa fa-pencil me-1"></i> Edit Profile
                    </Link>
                  </div>
                  <div className="x_content p-2">
                    <table className="table table-bordered mb-0" style={{ borderColor: "#f1f5f9" }}>
                      <tbody>
                        <tr><th style={{ width: "32%", background: "#fdf2f8", color: "#831843", fontWeight: "600" }}>Religion</th><td><strong>{valOrMuted(user.religion?.name)}</strong></td></tr>
                        <tr><th style={{ background: "#fdf2f8", color: "#831843", fontWeight: "600" }}>Caste</th><td>{valOrMuted(user.caste?.name)}</td></tr>
                        <tr><th style={{ background: "#fdf2f8", color: "#831843", fontWeight: "600" }}>Sub Caste</th><td>{valOrMuted(user.sub_caste?.name || user.sub_caste)}</td></tr>
                        <tr><th style={{ background: "#fdf2f8", color: "#831843", fontWeight: "600" }}>Gotra</th><td>{valOrMuted(user.gotra?.name || user.gotra)}</td></tr>
                        <tr><th style={{ background: "#fdf2f8", color: "#831843", fontWeight: "600" }}>Diet</th><td>{valOrMuted(user.diet?.name)}</td></tr>
                        <tr><th style={{ background: "#fdf2f8", color: "#831843", fontWeight: "600" }}>Kundali Dosh</th><td>{valOrMuted(user.dosh)}</td></tr>
                        <tr><th style={{ background: "#fdf2f8", color: "#831843", fontWeight: "600" }}>Manglik</th><td>{valOrMuted(user.manglik)}</td></tr>
                        <tr><th style={{ background: "#fdf2f8", color: "#831843", fontWeight: "600" }}>Birth Place</th><td>{user.birth_city?.name || user.birth_state?.name ? `${user.birth_city?.name || ''}, ${user.birth_state?.name || ''}` : <span className="text-muted fst-italic">Not provided</span>}</td></tr>
                        <tr><th style={{ background: "#fdf2f8", color: "#831843", fontWeight: "600" }}>Birth Time</th><td>{user.birth_hour ? `${user.birth_hour}:${user.birth_minute || '00'} ${user.birth_am || ''}` : <span className="text-muted fst-italic">Not provided</span>}</td></tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* 4. EDUCATION & CAREER */}
              {activeTab === "education" && (
                <div className="x_panel shadow-sm" style={{ borderRadius: "12px", border: "none", background: "#ffffff" }}>
                  <div className="x_title d-flex justify-content-between align-items-center" style={{ borderBottom: "2px solid #fce7f3", paddingBottom: "12px" }}>
                    <h4 style={{ margin: 0, fontWeight: "700", color: "#b82e66", fontSize: "16px" }}>
                      <i className="fa fa-graduation-cap me-2"></i> Education & Career Details
                    </h4>
                    <Link to={`/user/edit-member/${user._id}`} className="btn btn-sm text-white" style={{ background: "linear-gradient(135deg, #b82e66 0%, #8e1948 100%)", borderRadius: "20px", fontWeight: "600", fontSize: "12px", padding: "4px 14px" }}>
                      <i className="fa fa-pencil me-1"></i> Edit Profile
                    </Link>
                  </div>
                  <div className="x_content p-2">
                    <table className="table table-bordered mb-0" style={{ borderColor: "#f1f5f9" }}>
                      <tbody>
                        <tr><th style={{ width: "32%", background: "#fdf2f8", color: "#831843", fontWeight: "600" }}>Highest Degree</th><td><strong>{valOrMuted(user.highest_degree?.name)}</strong></td></tr>
                        <tr><th style={{ background: "#fdf2f8", color: "#831843", fontWeight: "600" }}>Undergraduate Degree</th><td>{valOrMuted(user.ug_degree?.name)}</td></tr>
                        <tr><th style={{ background: "#fdf2f8", color: "#831843", fontWeight: "600" }}>Postgraduate Degree</th><td>{valOrMuted(user.pg_degree?.name)}</td></tr>
                        <tr><th style={{ background: "#fdf2f8", color: "#831843", fontWeight: "600" }}>UG College Name</th><td>{valOrMuted(user.ug_college_name)}</td></tr>
                        <tr><th style={{ background: "#fdf2f8", color: "#831843", fontWeight: "600" }}>PG College Name</th><td>{valOrMuted(user.pg_college_name)}</td></tr>
                        <tr><th style={{ background: "#fdf2f8", color: "#831843", fontWeight: "600" }}>Employed In (Sector)</th><td>{valOrMuted(user.working_with?.name)}</td></tr>
                        <tr><th style={{ background: "#fdf2f8", color: "#831843", fontWeight: "600" }}>Occupation</th><td>{valOrMuted(user.occupation?.name)}</td></tr>
                        <tr><th style={{ background: "#fdf2f8", color: "#831843", fontWeight: "600" }}>Company / Organization</th><td>{valOrMuted(user.organization_name)}</td></tr>
                        <tr><th style={{ background: "#fdf2f8", color: "#831843", fontWeight: "600" }}>Annual Income</th><td><strong>{user.annual_income ? formatIncome(user.annual_income) : valOrMuted(null)}</strong></td></tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* 5. FAMILY & PARTNER PREFERENCES */}
              {activeTab === "family" && (
                <div className="row">
                  <div className="col-md-6 col-sm-12">
                    <div className="x_panel shadow-sm" style={{ borderRadius: "12px", border: "none", background: "#ffffff" }}>
                      <div className="x_title d-flex justify-content-between align-items-center" style={{ borderBottom: "2px solid #fce7f3", paddingBottom: "10px" }}>
                        <h4 style={{ margin: 0, fontWeight: "700", color: "#b82e66", fontSize: "15px" }}>
                          <i className="fa fa-users me-2"></i> Family Details
                        </h4>
                        <Link to={`/user/edit-member/${user._id}`} className="btn btn-sm text-white" style={{ background: "linear-gradient(135deg, #b82e66 0%, #8e1948 100%)", borderRadius: "20px", fontWeight: "600", fontSize: "11px", padding: "3px 12px" }}>
                          <i className="fa fa-pencil me-1"></i> Edit
                        </Link>
                      </div>
                      <div className="x_content p-2">
                        <table className="table table-bordered mb-0" style={{ borderColor: "#f1f5f9" }}>
                          <tbody>
                            <tr><th style={{ width: "40%", background: "#fdf2f8", color: "#831843", fontWeight: "600" }}>Family Type</th><td>{valOrMuted(user.family_type)}</td></tr>
                            <tr><th style={{ background: "#fdf2f8", color: "#831843", fontWeight: "600" }}>Family Value</th><td>{valOrMuted(user.family_value)}</td></tr>
                            <tr><th style={{ background: "#fdf2f8", color: "#831843", fontWeight: "600" }}>Father's Status</th><td>{valOrMuted(user.father_status)}</td></tr>
                            <tr><th style={{ background: "#fdf2f8", color: "#831843", fontWeight: "600" }}>Mother's Status</th><td>{valOrMuted(user.mother_status)}</td></tr>
                            <tr><th style={{ background: "#fdf2f8", color: "#831843", fontWeight: "600" }}>Brothers</th><td>{user.no_of_brother || "0"} (Married: {user.married_brother || "0"})</td></tr>
                            <tr><th style={{ background: "#fdf2f8", color: "#831843", fontWeight: "600" }}>Sisters</th><td>{user.no_of_sister || "0"} (Married: {user.married_sister || "0"})</td></tr>
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>

                  <div className="col-md-6 col-sm-12">
                    <div className="x_panel shadow-sm" style={{ borderRadius: "12px", border: "none", background: "#ffffff" }}>
                      <div className="x_title d-flex justify-content-between align-items-center" style={{ borderBottom: "2px solid #fce7f3", paddingBottom: "10px" }}>
                        <h4 style={{ margin: 0, fontWeight: "700", color: "#b82e66", fontSize: "15px" }}>
                          <i className="fa fa-heart me-2"></i> Partner Preferences
                        </h4>
                        <Link to={`/user/edit-member/${user._id}`} className="btn btn-sm text-white" style={{ background: "linear-gradient(135deg, #b82e66 0%, #8e1948 100%)", borderRadius: "20px", fontWeight: "600", fontSize: "11px", padding: "3px 12px" }}>
                          <i className="fa fa-pencil me-1"></i> Edit
                        </Link>
                      </div>
                      <div className="x_content p-2">
                        <table className="table table-bordered mb-0" style={{ borderColor: "#f1f5f9" }}>
                          <tbody>
                            <tr><th style={{ width: "40%", background: "#fdf2f8", color: "#831843", fontWeight: "600" }}>Age Range</th><td>{user.partner_age_from ? `${user.partner_age_from} - ${user.partner_age_to} Yrs` : "Any"}</td></tr>
                            <tr><th style={{ background: "#fdf2f8", color: "#831843", fontWeight: "600" }}>Height Range</th><td>{user.partner_height_from ? `${formatHeight(user.partner_height_from)} - ${formatHeight(user.partner_height_to)}` : "Any"}</td></tr>
                            <tr><th style={{ background: "#fdf2f8", color: "#831843", fontWeight: "600" }}>Marital Status</th><td>{valOrMuted(user.partner_marital_status?.name || (Array.isArray(user.partner_marital_status) ? user.partner_marital_status.map(m => m?.name || m).join(", ") : "Any"))}</td></tr>
                            <tr><th style={{ background: "#fdf2f8", color: "#831843", fontWeight: "600" }}>Religion / Caste</th><td>{user.partner_religion?.name || "Any"} / {user.partner_caste?.name || "Any"}</td></tr>
                            <tr><th style={{ background: "#fdf2f8", color: "#831843", fontWeight: "600" }}>Sub Caste / Gotra</th><td>{user.partner_sub_caste?.name || user.partner_sub_caste || "Any"} / {user.partner_gotra?.name || user.partner_gotra || "Any"}</td></tr>
                            <tr><th style={{ background: "#fdf2f8", color: "#831843", fontWeight: "600" }}>Mother Tongue</th><td>{user.partner_mother_tongue?.name || user.partner_language?.name || "Any"}</td></tr>
                            <tr><th style={{ background: "#fdf2f8", color: "#831843", fontWeight: "600" }}>Location</th><td>{user.partner_city?.name || "Any"}, {user.partner_state?.name || "Any"}</td></tr>
                            <tr><th style={{ background: "#fdf2f8", color: "#831843", fontWeight: "600" }}>Education / Career</th><td>{user.partner_education?.name || "Any"} / {user.partner_occupation?.name || "Any"}</td></tr>
                            <tr><th style={{ background: "#fdf2f8", color: "#831843", fontWeight: "600" }}>Employed In (Sector)</th><td>{user.partner_working_as?.name || "Any"}</td></tr>
                            <tr><th style={{ background: "#fdf2f8", color: "#831843", fontWeight: "600" }}>Annual Income</th><td>{user.partner_income_from ? `${formatIncome(user.partner_income_from)} - ${formatIncome(user.partner_income_to)}` : "Any"}</td></tr>
                            <tr><th style={{ background: "#fdf2f8", color: "#831843", fontWeight: "600" }}>Diet / Habits</th><td>{user.partner_diet?.name || "Any"} | Drink: {user.partner_drinking || "No"} | Smoke: {user.partner_smoking || "No"}</td></tr>
                            <tr><th style={{ background: "#fdf2f8", color: "#831843", fontWeight: "600" }}>Have Children</th><td>{valOrMuted(user.partner_have_children)}</td></tr>
                            <tr><th style={{ background: "#fdf2f8", color: "#831843", fontWeight: "600" }}>Family Type / Value</th><td>{user.partner_family_type || "Any"} / {user.partner_family_value || "Any"}</td></tr>
                            <tr><th style={{ background: "#fdf2f8", color: "#831843", fontWeight: "600" }}>Profile Managed By</th><td>{valOrMuted(user.partner_managed_by)}</td></tr>
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* 6. PHOTOS GALLERY */}
              {activeTab === "photos" && (
                <div className="x_panel shadow-sm" style={{ borderRadius: "12px", border: "none", background: "#ffffff" }}>
                  <div className="x_title d-flex justify-content-between align-items-center" style={{ borderBottom: '2px solid #fce7f3', paddingBottom: '12px' }}>
                    <h4 style={{ margin: 0, fontWeight: "700", color: "#b82e66", fontSize: "16px" }}>
                      <i className="fa fa-picture-o me-2"></i> Uploaded Member Photos
                    </h4>
                    <Link to={`/user/edit-member/${user._id}`} className="btn btn-sm text-white" style={{ background: "linear-gradient(135deg, #b82e66 0%, #8e1948 100%)", borderRadius: "20px", fontWeight: "600", fontSize: "12px", padding: "4px 14px" }}>
                      <i className="fa fa-pencil me-1"></i> Manage Photos
                    </Link>
                  </div>
                  <div className="x_content p-3">
                    <div className="row g-4">
                      {photoList.map((slot) => {
                        const isUploading = uploadingPhotoIndex === slot.index;
                        return (
                          <div key={slot.key} className="col-md-3 col-sm-6">
                            <div
                              style={{
                                padding: "14px",
                                background: slot.file ? "#fdf2f8" : "#f8fafc",
                                borderRadius: "12px",
                                border: slot.file ? "1px solid #fbcfe8" : "1px dashed #cbd5e1",
                                boxShadow: "0 4px 10px rgba(0,0,0,0.04)",
                                textAlign: "center"
                              }}
                            >
                              <span
                                className="badge mb-2"
                                style={{
                                  background: slot.index === 0 ? "linear-gradient(135deg, #b82e66 0%, #8e1948 100%)" : "#475569",
                                  color: "#fff",
                                  fontSize: "11px",
                                  borderRadius: "20px",
                                  padding: "4px 10px"
                                }}
                              >
                                {slot.label}
                              </span>

                              <div style={{ width: "100%", height: "200px", borderRadius: "8px", overflow: "hidden", background: "#fff", display: "flex", alignItems: "center", justifyContent: "center", border: "1px solid #e2e8f0", marginBottom: "10px" }}>
                                {slot.file ? (
                                  <img
                                    src={getImageUrl(slot.file)}
                                    alt={slot.label}
                                    style={{ width: "100%", height: "100%", objectFit: "cover" }}
                                    onError={(e) => { e.target.onerror = null; e.target.src = getDefaultAvatar(user.gender); }}
                                  />
                                ) : (
                                  <div className="text-muted text-center p-3">
                                    <i className="fa fa-image fa-2x mb-1" style={{ color: "#cbd5e1" }}></i>
                                    <div style={{ fontSize: "11px" }}>No Photo</div>
                                  </div>
                                )}
                              </div>

                              <div className="d-flex gap-2 justify-content-center flex-wrap">
                                <label
                                  className="btn btn-sm text-white m-0"
                                  style={{
                                    borderRadius: "20px",
                                    background: "#2563eb",
                                    fontSize: "11px",
                                    padding: "4px 12px",
                                    cursor: isUploading ? "not-allowed" : "pointer"
                                  }}
                                >
                                  {isUploading ? (
                                    <>
                                      <span className="spinner-border spinner-border-sm me-1" role="status"></span>
                                      Uploading...
                                    </>
                                  ) : (
                                    <>
                                      <i className="fa fa-upload me-1"></i> {slot.file ? "Replace" : "Upload"}
                                    </>
                                  )}
                                  <input
                                    type="file"
                                    accept="image/*"
                                    disabled={isUploading}
                                    style={{ display: "none" }}
                                    onChange={(e) => handleUploadPhoto(e, slot.index)}
                                  />
                                </label>

                                {slot.file && (
                                  <button
                                    type="button"
                                    onClick={() => handleDeletePhoto(slot.index)}
                                    className="btn btn-sm btn-danger"
                                    style={{ borderRadius: "20px", fontSize: "11px", padding: "4px 12px" }}
                                  >
                                    <i className="fa fa-trash me-1"></i> Delete
                                  </button>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              )}
            </>
          ) : null}
        </div>
        <Footer />
      </div>
    </div>
  );
}
